import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  getMenuOptions,
  getPosMenu,
  updatePosMenu,
  getMenuItems,
  getMenuItemModifiers,
  getAllItems,
  getUpSellAndCrossSells,
  getUpSellAndCrossSell,
  createUpSellAndCrossSell,
  updateUpSellAndCrossSell,
  deleteUpSellAndCrossSell,
  getAlternativeNames,
  getAlternativeName,
  createAlternativeName,
  updateAlternativeName,
  deleteAlternativeName
} from './api';
import { AlternativeNameItem, Item, UpsellAndCrossSell } from './types';
import { queryClient } from '@/providers/AllProviders';
import { useNavigate } from 'react-router-dom';

export function useGetUpSellAndCrossSells(
  integrationId?: number,
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: UpsellAndCrossSell[] }, Error>
) {
  return useQuery<{ data: UpsellAndCrossSell[] }>({
    queryKey: ['upsell_cross_sells', integrationId, params],
    queryFn: () => getUpSellAndCrossSells(integrationId!, params),
    enabled: Boolean(integrationId),
    ...options
  });
}

export function useGetUpSellAndCrossSell(
  integrationId?: number,
  upSellAndCrossSellId?: number,
  params?: Record<string, any>,
  options?: UseQueryOptions<UpsellAndCrossSell, Error>
) {
  return useQuery<UpsellAndCrossSell>({
    queryKey: ['upsell_cross_sell', integrationId, upSellAndCrossSellId],
    queryFn: () => getUpSellAndCrossSell(integrationId!, upSellAndCrossSellId!, params),
    enabled: Boolean(integrationId && upSellAndCrossSellId),
    ...options
  });
}

export function useCreateUpSellAndCrossSell(
  integrationId: number,
  upSellAndCrossSellId: number,
  options?: UseMutationOptions<any, Error, any>
) {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (payload: any) => createUpSellAndCrossSell(integrationId, upSellAndCrossSellId, payload),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: ['upsell_cross_sells', integrationId]
      });
      options?.onSuccess?.(...args);
      navigate(-1);
    }
  });
}

export function useUpdateUpSellAndCrossSell(
  integrationId: number,
  upSellAndCrossSellId: number,
  options?: UseMutationOptions<any, Error, any>
) {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (payload: any) => updateUpSellAndCrossSell(integrationId, upSellAndCrossSellId, payload),
    ...options,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['upsell_cross_sells', integrationId]
      });
      queryClient.refetchQueries({
        queryKey: ['upsell_cross_sell', integrationId, upSellAndCrossSellId]
      });
      navigate(-1);
    }
  });
}

export function useDeleteUpSellAndCrossSell(integrationId: number, options?: UseMutationOptions<any, Error, number>) {
  return useMutation({
    mutationFn: (upSellAndCrossSellId: number) => deleteUpSellAndCrossSell(integrationId, upSellAndCrossSellId),
    ...options,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['upsell_cross_sells', integrationId]
      });
    }
  });
}

export function useGetMenuOptions(
  locationId?: number,
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: Item[] }, Error>
) {
  return useQuery<{ data: Item[] }>({
    queryKey: ['menu_options', locationId, params],
    queryFn: () => getMenuOptions(locationId!, params),
    enabled: Boolean(locationId),
    ...options
  });
}

export function useUpdatePosMenu(
  locationId?: number,
  options?: UseMutationOptions<{ data: Item }, Error, { menuId: number; offer_menu: boolean }>
) {
  return useMutation({
    mutationFn: ({ menuId, offer_menu }: { menuId: number; offer_menu: boolean }) =>
      updatePosMenu(locationId!, menuId, { menu: { offer_menu } }),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: ['menu_options', locationId]
      });
      options?.onSuccess?.(...args);
    }
  });
}

export function useGetPosMenu(
  locationId?: number,
  menuId?: number,
  options?: Omit<UseQueryOptions<unknown, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<unknown, Error>({
    queryKey: ['pos_menu_json', locationId, menuId],
    queryFn: () => getPosMenu(locationId!, menuId!),
    enabled: Boolean(locationId && menuId),
    ...options
  });
}

export function useGetMenuItems(
  locationId?: number,
  menuId?: number,
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: Item[] }, Error>
) {
  return useQuery<{ data: Item[] }>({
    queryKey: ['menu_items', locationId, menuId, params],
    queryFn: () => getMenuItems(locationId!, menuId!, params),
    enabled: Boolean(locationId && menuId),
    ...options
  });
}

export function useGetMenuItemModifiers(
  locationId?: number,
  menuId?: number,
  menuItemId?: number,
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: Item[] }, Error>
) {
  return useQuery<{ data: Item[] }>({
    queryKey: ['menu_item_modifiers', locationId, menuId, menuItemId, params],
    queryFn: () => getMenuItemModifiers(locationId!, menuId!, menuItemId!, params),
    enabled: Boolean(locationId && menuId && menuItemId),
    ...options
  });
}

export function useGetAllItems(
  locationId?: number,
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: Item[] }, Error>
) {
  return useQuery<{ data: Item[] }>({
    queryKey: ['all_items', locationId, params],
    queryFn: () => getAllItems(locationId!, params),
    enabled: Boolean(locationId),
    ...options
  });
}

export function useGetAlternativeNames(
  integrationId?: number,
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: AlternativeNameItem[] }, Error>
) {
  return useQuery<{ data: AlternativeNameItem[] }>({
    queryKey: ['alternative_names', integrationId, params],
    queryFn: () => getAlternativeNames(integrationId!, params),
    enabled: Boolean(integrationId),
    ...options
  });
}

export function useGetAlternativeName(
  integrationId?: number,
  alternativeNameId?: number,
  params?: Record<string, any>,
  options?: UseQueryOptions<AlternativeNameItem, Error>
) {
  return useQuery<AlternativeNameItem>({
    queryKey: ['alternative_name', integrationId, alternativeNameId],
    queryFn: () => getAlternativeName(integrationId!, alternativeNameId!, params),
    enabled: Boolean(integrationId && alternativeNameId),
    ...options
  });
}

export function useCreateAlternativeName(
  integrationId: number,
  options?: UseMutationOptions<AlternativeNameItem, Error, any>
) {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (payload: any) => createAlternativeName(integrationId, payload),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: ['alternative_names', integrationId]
      });
      options?.onSuccess?.(...args);
      navigate(-1);
    }
  });
}

export function useUpdateAlternativeName(
  integrationId: number,
  alternativeNameId: number,
  options?: UseMutationOptions<AlternativeNameItem, Error, any>
) {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (payload: any) => updateAlternativeName(integrationId, alternativeNameId, payload),
    ...options,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['alternative_names']
      });
      queryClient.refetchQueries({
        queryKey: ['alternative_name']
      });
      navigate(-1);
    }
  });
}

export function useDeleteAlternativeName(integrationId: number, options?: UseMutationOptions<any, Error, number>) {
  return useMutation({
    mutationFn: (alternativeNameId: number) => deleteAlternativeName(integrationId, alternativeNameId),
    ...options,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['alternative_names', integrationId]
      });
    }
  });
}
